import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Clock, Save, CalendarOff, Plus, RotateCcw, Copy } from "lucide-react";

const DAYS = [
  ["0", "Monday"], ["1", "Tuesday"], ["2", "Wednesday"], ["3", "Thursday"],
  ["4", "Friday"], ["5", "Saturday"], ["6", "Sunday"],
];

const blankSchedule = () =>
  Object.fromEntries(DAYS.map(([k]) => [k, { open: false, open_time: "09:00", close_time: "17:00" }]));

export default function SiteOperating({ venueId, canManageSchedule, canManageClosures }) {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState(blankSchedule());
  const [hasSchedule, setHasSchedule] = useState(false);
  const [closures, setClosures] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ state: "closed", start_date: "", end_date: "", reason: "", note: "", custom_open_time: "10:00", custom_close_time: "22:00" });

  const loadSchedule = () => api.get(`/sites/${venueId}/schedule`).then((r) => {
    setHasSchedule(r.data.has_schedule);
    if (r.data.operating_schedule) setSchedule({ ...blankSchedule(), ...r.data.operating_schedule });
  }).catch(() => {});
  const loadClosures = () => api.get(`/sites/${venueId}/closures`).then((r) => setClosures(r.data)).catch(() => {});

  useEffect(() => {
    if (venueId) { loadSchedule(); loadClosures(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  const setDay = (k, patch) => setSchedule((s) => ({ ...s, [k]: { ...s[k], ...patch } }));
  const copyMondayToWeek = () => {
    const mon = schedule["0"];
    setSchedule((s) => Object.fromEntries(DAYS.map(([k]) => [k, { ...mon }])));
    toast.success("Copied Monday's hours to every day");
  };

  const saveSchedule = async () => {
    setSaving(true);
    try {
      await api.put(`/sites/${venueId}/schedule`, { operating_schedule: schedule });
      toast.success("Opening hours saved");
      setHasSchedule(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save opening hours"); }
    finally { setSaving(false); }
  };

  const addClosure = async () => {
    if (!form.start_date) return toast.error("Choose a start date");
    try {
      await api.post(`/sites/${venueId}/closures`, {
        ...form, end_date: form.end_date || form.start_date, source: "planned",
      });
      toast.success("Override saved");
      setForm({ state: "closed", start_date: "", end_date: "", reason: "", note: "", custom_open_time: "10:00", custom_close_time: "22:00" });
      loadClosures();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save override"); }
  };

  const reverseClosure = async (cid) => {
    try { await api.post(`/sites/${venueId}/closures/${cid}/reverse`, {}); toast.success("Override reversed"); loadClosures(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not reverse"); }
  };

  const stateLabel = { closed: "Closed", custom_hours: "Custom hours", open_exception: "Open (exception)" };

  return (
    <div className="space-y-6" data-testid="site-operating">
      {/* Weekly schedule */}
      <div className="cc-card p-6 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-display font-bold text-slate-800"><Clock size={18} className="text-brand" /> Site opening hours</div>
          {canManageSchedule && <button onClick={copyMondayToWeek} data-testid="copy-monday" className="text-xs text-slate-500 hover:text-brand flex items-center gap-1"><Copy size={13} /> Copy Monday to all</button>}
        </div>
        {!hasSchedule && <p className="text-xs text-amber-600">No schedule configured yet — CaterCTRL treats this site as always reporting until you set hours.</p>}
        <div className="space-y-2">
          {DAYS.map(([k, name]) => (
            <div key={k} className="flex items-center gap-3 flex-wrap" data-testid={`day-row-${k}`}>
              <label className="flex items-center gap-2 w-32 shrink-0">
                <input type="checkbox" checked={schedule[k].open} disabled={!canManageSchedule}
                  onChange={(e) => setDay(k, { open: e.target.checked })} data-testid={`day-open-${k}`} className="accent-brand" />
                <span className="text-sm font-medium text-slate-700">{name}</span>
              </label>
              {schedule[k].open ? (
                <div className="flex items-center gap-2">
                  <Input type="time" value={schedule[k].open_time || ""} disabled={!canManageSchedule}
                    onChange={(e) => setDay(k, { open_time: e.target.value })} data-testid={`day-opentime-${k}`} className="w-28 h-8" />
                  <span className="text-slate-400 text-sm">to</span>
                  <Input type="time" value={schedule[k].close_time || ""} disabled={!canManageSchedule}
                    onChange={(e) => setDay(k, { close_time: e.target.value })} data-testid={`day-closetime-${k}`} className="w-28 h-8" />
                </div>
              ) : <span className="text-sm text-slate-400">Closed</span>}
            </div>
          ))}
        </div>
        {canManageSchedule && <Button onClick={saveSchedule} disabled={saving} data-testid="save-schedule" className="bg-brand hover:bg-brand-hover rounded-xl"><Save size={15} className="mr-1" /> Save hours</Button>}
      </div>

      {/* Planned closures / special hours */}
      <div className="cc-card p-6 space-y-3" data-testid="closures-card">
        <div className="flex items-center gap-2 font-display font-bold text-slate-800"><CalendarOff size={18} className="text-brand" /> Planned closures & special hours</div>
        {closures.length === 0 && <div className="text-sm text-slate-400">No upcoming closures or overrides.</div>}
        {closures.map((c) => (
          <div key={c.closure_id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm" data-testid={`closure-${c.closure_id}`}>
            <div className="min-w-0">
              <span className="font-medium text-slate-700">{stateLabel[c.state] || c.state}</span>
              <span className="text-slate-500"> · {c.start_date}{c.end_date !== c.start_date ? ` → ${c.end_date}` : ""}</span>
              {c.source === "unexpected" && <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-600">emergency</span>}
              {c.reason ? <div className="text-xs text-slate-400">{c.reason}{c.note ? ` — ${c.note}` : ""}</div> : null}
            </div>
            {canManageClosures && <button onClick={() => reverseClosure(c.closure_id)} data-testid={`reverse-${c.closure_id}`} className="text-slate-400 hover:text-brand flex items-center gap-1 text-xs shrink-0"><RotateCcw size={13} /> Reverse</button>}
          </div>
        ))}

        {canManageClosures && (
          <div className="border-t pt-3 space-y-2" data-testid="add-closure-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-slate-600">Type</Label>
                <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                  <SelectTrigger data-testid="closure-state" className="mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="custom_hours">Custom hours</SelectItem>
                    <SelectItem value="open_exception">Open (normally closed)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Reason</Label>
                <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="closure-reason" className="mt-1 h-9" placeholder="e.g. Christmas Day" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-slate-600">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="closure-start" className="mt-1 h-9" /></div>
              <div><Label className="text-xs text-slate-600">End date (optional)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="closure-end" className="mt-1 h-9" /></div>
            </div>
            {form.state === "custom_hours" && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs text-slate-600">Open</Label><Input type="time" value={form.custom_open_time} onChange={(e) => setForm({ ...form, custom_open_time: e.target.value })} data-testid="closure-copen" className="mt-1 h-9" /></div>
                <div><Label className="text-xs text-slate-600">Close</Label><Input type="time" value={form.custom_close_time} onChange={(e) => setForm({ ...form, custom_close_time: e.target.value })} data-testid="closure-cclose" className="mt-1 h-9" /></div>
              </div>
            )}
            <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="closure-note" placeholder="Optional note" className="text-sm" />
            <Button onClick={addClosure} data-testid="add-closure" className="bg-brand hover:bg-brand-hover"><Plus size={14} className="mr-1" /> Add override</Button>
          </div>
        )}
      </div>
    </div>
  );
}
